import axios from 'axios';
import { API_CONFIG, getV1EndpointUrl } from '../config/api.js';
import { v1Api } from './api.js';
import authService from './authService.js';
import { handleTokenExpiry, shouldSkipAutoLogout } from '../utils/authUtils.js';
import { hasLocalStorage, getLocalStorage } from '../utils/browserCheck.js';
const API_BASE_URL = API_CONFIG.BASE_URL;
// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_CONFIG.TIMEOUT, // Changed from 10000 to use config
  headers: {
    'Content-Type': 'application/json',
  },
});
// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    // Add client type header
    config.headers[API_CONFIG.HEADERS.CLIENT_TYPE] = 'web';
    // Add JWT token to Authorization header
    // Check if localStorage is available (browser environment)
    const storage = getLocalStorage();
    if (storage) {
      const token = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';

      // Skip auto-logout only for actual auth endpoints (login, register, etc.)
      // All other endpoints (including booking-related) should trigger logout on token expiry
      if (shouldSkipAutoLogout(url)) {
        console.log(`🔑 [apiService] Skipping auto-logout for auth endpoint: ${url}`);
        return Promise.reject(error);
      }

      // Check if error message indicates token expiry
      const errorMessage = error.response?.data?.message || '';
      const isTokenExpired =
        errorMessage.toLowerCase().includes('expired') ||
        errorMessage.toLowerCase().includes('token has expired') ||
        errorMessage.toLowerCase().includes('invalid token') ||
        errorMessage.toLowerCase().includes('unauthorized');

      if (isTokenExpired) {
        console.warn(`🚨 [apiService] Token expired detected for: ${url}`);
        // Handle token expiry with automatic logout
        await handleTokenExpiry(`Token expired for endpoint: ${url}`);
        // handleTokenExpiry will redirect, but return a rejected promise to prevent further processing
        return Promise.reject(new Error('Token expired - redirecting to login'));
      }

      // For other 401 errors, also logout (invalid/missing token)
      console.warn(`🚨 [apiService] 401 Unauthorized for: ${url}`);
      await handleTokenExpiry(`Unauthorized access for endpoint: ${url}`);
      return Promise.reject(new Error('Unauthorized - redirecting to login'));
    }
    return Promise.reject(error);
  }
);
const apiService = {
  // ============================================================================
  // BOOKING METHODS
  // ============================================================================
  async createBooking(bookingData) {
    try {
      const response = await apiClient.post('/bookings', bookingData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getAllBookings(params = {}) {
    try {

      // Extract hospital_id and operator_id from params if provided
      const { hospital_id, operator_id, ...queryParams } = params;

      // Use appropriate endpoint based on whether hospital_id or operator_id is provided
      let endpoint = '/bookings/booking-history';  // Get all bookings with status filtering
      if (hospital_id && hospital_id !== null && hospital_id !== undefined && hospital_id !== 'null') {
        endpoint = `/bookings/${hospital_id}`;
      } else if (operator_id && operator_id !== null && operator_id !== undefined && operator_id !== 'null') {
        endpoint = `/bookings/operator/${operator_id}`;
      }

      console.log('getAllBookings API call:', { endpoint, queryParams });

      const response = await apiClient.get(endpoint, { params: queryParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getBookingById(bookingId) {
    try {
      const response = await apiClient.get(`/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateBookingStatus(bookingId, statusData) {
    try {
      const response = await apiClient.put(`/bookings/${bookingId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async cancelBooking(bookingId, cancelData) {
    try {
      const response = await apiClient.put(`/bookings/${bookingId}/cancel`, cancelData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getBookingHistory(params = {}) {
    try {

      // Use the correct endpoint for booking history
      const response = await apiClient.get('/bookings/booking-history', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getBookingStatusHistory(bookingId) {
    try {
      const response = await apiClient.get(`/bookings/${bookingId}/status-history`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async findBestAmbulance(matchData) {
    try {
      const response = await apiClient.post('/bookings/smart-match', matchData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateAmbulanceLocation(ambulanceId, locationData) {
    try {
      const response = await apiClient.put(`/bookings/ambulance/${ambulanceId}/location`, locationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateDriverLocation(driverId, locationData) {
    try {
      const response = await apiClient.put(`/bookings/driver/${driverId}/location`, locationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getLiveTrackingData(bookingId) {
    try {
      const response = await apiClient.get(`/bookings/${bookingId}/tracking`);
      const result = response.data;

      // Transform response to standardized format if needed
      if (result && result.data) {
        const trackingData = result.data;

        // Extract and validate driver location
        let driverLocation = null;
        const ambulanceLocation = trackingData.ambulance_location || trackingData.ambulanceLocation || trackingData.driverLocation || trackingData.driver_location;

        if (ambulanceLocation) {
          const lat = parseFloat(ambulanceLocation.latitude || ambulanceLocation.lat);
          const lng = parseFloat(ambulanceLocation.longitude || ambulanceLocation.lng || ambulanceLocation.lon);

          if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            driverLocation = {
              latitude: lat,
              longitude: lng,
              lastUpdated: ambulanceLocation.last_updated || ambulanceLocation.lastUpdated || ambulanceLocation.timestamp || new Date().toISOString(),
              heading: ambulanceLocation.heading || null,
              bearing: ambulanceLocation.bearing || null,
              accuracy: ambulanceLocation.accuracy || null
            };
          }
        }

        // Extract ETA
        let eta = trackingData.eta_minutes || trackingData.eta;
        if (eta && typeof eta === 'number' && eta > 0) {
          // Convert minutes to future timestamp
          eta = new Date(Date.now() + eta * 60 * 1000);
        }

        // Extract pickup location
        let pickupLocation = null;
        const pickup = trackingData.pickup_location || trackingData.pickupLocation;
        if (pickup) {
          const pickupLat = parseFloat(pickup.latitude || pickup.lat);
          const pickupLng = parseFloat(pickup.longitude || pickup.lng);
          if (!isNaN(pickupLat) && !isNaN(pickupLng) && isFinite(pickupLat) && isFinite(pickupLng)) {
            pickupLocation = {
              latitude: pickupLat,
              longitude: pickupLng,
              address: pickup.address || 'Pickup Location'
            };
          }
        }

        // Extract dropoff location
        let dropoffLocation = null;
        const dropoff = trackingData.dropoff_location || trackingData.dropoffLocation;
        if (dropoff) {
          const dropoffLat = parseFloat(dropoff.latitude || dropoff.lat);
          const dropoffLng = parseFloat(dropoff.longitude || dropoff.lng);
          if (!isNaN(dropoffLat) && !isNaN(dropoffLng) && isFinite(dropoffLat) && isFinite(dropoffLng)) {
            dropoffLocation = {
              latitude: dropoffLat,
              longitude: dropoffLng,
              address: dropoff.address || 'Dropoff Location'
            };
          }
        }

        // Ensure standardized format
        result.data = {
          bookingId: trackingData.booking_id || trackingData.bookingId || bookingId,
          status: trackingData.status || trackingData.booking_status || 'PENDING',
          statusId: trackingData.status_id || trackingData.statusId,
          bookingType: trackingData.booking_type || trackingData.bookingType || 'INSTANT',
          scheduledDateTime: trackingData.scheduled_date_time || trackingData.scheduledDateTime || null,
          pickupLocation: pickupLocation,
          dropoffLocation: dropoffLocation,
          createdAt: trackingData.created_at || trackingData.createdAt || new Date().toISOString(),
          hospital: trackingData.hospital || null,
          driver: trackingData.driver || null,
          ambulance: trackingData.vehicle || trackingData.ambulance || null,
          tracking: {
            eta: eta,
            distance: trackingData.distance_km || trackingData.distance || null,
            driverLocation: driverLocation,
            ambulanceLocation: driverLocation,
            lastUpdated: driverLocation?.lastUpdated || new Date().toISOString()
          },
          journey_direction: trackingData.journey_direction || trackingData.journeyDirection || trackingData.booking_direction || trackingData.direction,
        };
      }

      return result;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async estimateFare(pickupLocation, dropoffLocation, ambulanceType) {
    try {
      const fareEstimateUrl = getV1EndpointUrl('/v1/hospital/bookings/estimate-fare');
      console.log('💰 [apiService] Estimating fare');
      console.log('💰 [apiService] Fare estimate URL:', fareEstimateUrl);
      const response = await api.post(fareEstimateUrl, {
        pickupLocation,
        dropoffLocation,
        ambulanceType
      });
      console.log('💰 [apiService] Fare estimate response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [apiService] Fare estimate error:', error);
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // AUTH METHODS
  // ============================================================================
  async login(credentials) {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async logout() {
    try {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async forgotPassword(email) {
    // Route to appropriate endpoint based on user role
    // New endpoint: /v1/hospital/auth/forgot-password (for hospital users)
    // New endpoint: /v1/fleet/auth/forgot-password (for fleet users)
    // Old endpoint: /forgot-password (for other users)
    try {
      const storage = getLocalStorage();
      const userData = storage ? storage.getItem('aps_user_data') : null;
      const userRole = userData ? JSON.parse(userData)?.role : null;
      const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
        userRole === 'HOSPITAL_ADMIN' ||
        userRole === 'hospital';
      const isFleetRequest = userRole === 'TENANT_ADMIN' ||
        userRole === 'TENANT_USER';

      if (isHospitalRequest) {
        // Use hospital-specific forgot password endpoint
        // New endpoint: /v1/hospital/auth/forgot-password
        console.log('🏥 Using hospital forgot password endpoint');
        return await authService.hospitalForgotPassword(email);
      }

      if (isFleetRequest) {
        // Use fleet-specific forgot password endpoint
        // New endpoint: /v1/fleet/auth/forgot-password
        console.log('🚛 Using fleet forgot password endpoint');
        return await authService.fleetForgotPassword(email);
      }

      // Use standard endpoint for non-hospital/non-fleet users
      // Old endpoint: /forgot-password
      console.log('📋 Using standard forgot password endpoint');
      return await authService.forgotPassword(email);
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getProfile() {
    try {
      const response = await apiClient.get('/auth/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateProfile(profileData) {
    try {
      const response = await apiClient.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // HOSPITAL METHODS
  // ============================================================================
  async getHospitals(params = {}) {
    try {
      const response = await apiClient.get('/hospital', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getHospitalById(hospitalId) {
    try {
      const response = await apiClient.get(`/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateHospital(hospitalId, hospitalData) {
    try {
      const response = await apiClient.put(`/hospital/${hospitalId}`, hospitalData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // FLEET METHODS
  // ============================================================================
  async getFleets(params = {}) {
    try {
      const response = await apiClient.get('/fleet', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getFleetById(fleetId) {
    try {
      const response = await apiClient.get(`/fleet/${fleetId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateFleet(fleetId, fleetData) {
    try {
      const response = await apiClient.put(`/fleet/${fleetId}`, fleetData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // AMBULANCE METHODS
  // ============================================================================
  async getAmbulances(params = {}) {
    try {
      const response = await apiClient.get('/ambulances/ambulances', { params }); // ✅ Correct endpoint: /api/ambulances + /ambulances
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // Super Admin method to get all ambulances with pagination
  async getAllAmbulances(params = {}) {
    try {
      // Default pagination params
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        ...params
      };
      const response = await apiClient.get('/ambulances/ambulances', { params: queryParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getAmbulanceById(ambulanceId) {
    try {
      const response = await apiClient.get(`/ambulances/profile/${ambulanceId}`); // ✅ Fixed: added /profile
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateAmbulance(ambulanceId, ambulanceData) {
    try {
      const response = await apiClient.put(`/ambulances/profile/${ambulanceId}`, ambulanceData); // ✅ Fixed: added /profile
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // DRIVER METHODS
  // ============================================================================
  async getDrivers(params = {}) {
    try {
      const response = await apiClient.get('/driver/drivers', { params }); // ✅ Fixed: added /drivers
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // Super Admin method to get all drivers with pagination
  async getAllDrivers(params = {}) {
    try {
      // Default pagination params
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        ...params
      };
      const response = await apiClient.get('/driver/drivers', { params: queryParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getDriverById(driverId) {
    try {
      const response = await apiClient.get(`/driver/profile/${driverId}`); // ✅ Fixed: added /profile
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateDriver(driverId, driverData) {
    try {
      const response = await apiClient.put(`/driver/profile/${driverId}`, driverData); // ✅ Fixed: added /profile
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // AMBULANCE REGISTRATION METHODS
  // ============================================================================
  async registerAmbulance(ambulanceData) {
    try {
      const response = await apiClient.post('/ambulances/register', ambulanceData); // ✅ Fixed: /ambulances/register
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async bulkRegisterAmbulances(ambulancesData) {
    try {
      const response = await apiClient.post('/ambulances/bulk-register', ambulancesData); // ✅ Fixed: /ambulances/bulk-register
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getAmbulanceTypes() {
    try {
      const response = await apiClient.get('/ambulances/types'); // ✅ Fixed: /ambulances/types
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getEquipmentTypes() {
    try {
      const response = await apiClient.get('/ambulances/equipment-types'); // ✅ Fixed: /ambulances/equipment-types
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async assignDriverToAmbulance(vehicleId, assignmentData) {
    try {
      const response = await apiClient.put(`/ambulances/${vehicleId}/assign-driver`, assignmentData); // ✅ Fixed: /ambulances/
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async removeDriverFromAmbulance(vehicleId) {
    try {
      const response = await apiClient.put(`/ambulances/${vehicleId}/remove-driver`); // ✅ Fixed: /ambulances/
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateAmbulanceStatus(vehicleId, statusData) {
    try {
      const response = await apiClient.put(`/ambulances/${vehicleId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async deleteAmbulance(vehicleId) {
    try {
      const response = await apiClient.delete(`/ambulances/${vehicleId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getMyVehicles(params = {}) {
    try {
      // Use v1Api to avoid /api prefix for v1 endpoints
      const response = await v1Api.get('/v1/driver/my-vehicles', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async getMyDrivers(params = {}) {
    try {
      // Use v1Api to avoid /api prefix for v1 endpoints
      const response = await v1Api.get('/v1/driver/my-drivers', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async uploadAmbulanceDocument(vehicleId, documentData) {
    try {
      const response = await apiClient.post(`/ambulances/${vehicleId}/documents`, documentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async verifyAmbulanceDocument(documentId, verificationData) {
    try {
      const response = await apiClient.put(`/ambulances/documents/${documentId}/verify`, verificationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async createAmbulanceType(typeData) {
    try {
      const response = await apiClient.post('/ambulances/types', typeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async deleteAmbulance(vehicleId) {
    try {
      const response = await apiClient.delete(`/ambulances/${vehicleId}`); // ✅ Fixed: /ambulances/
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ============================================================================
  // DRIVER REGISTRATION METHODS
  // ============================================================================
  async registerDriver(driverData) {
    try {
      const response = await apiClient.post('/driver/register', driverData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async bulkRegisterDrivers(driversData) {
    try {
      const response = await apiClient.post('/driver/bulk-register', driversData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async deleteDriver(driverId) {
    try {
      const response = await apiClient.delete(`/driver/${driverId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // Drivers/vehicles created via Command Center's "Add & Assign" still
  // awaiting full registration (is_verified: false) — see driverService.
  // getPendingDriverRegistrations on the backend.
  async getPendingDriverRegistrations() {
    try {
      const response = await apiClient.get('/driver/pending-registrations');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async registerPendingDriver(driverId, formData) {
    try {
      const response = await apiClient.post(`/driver/pending-registrations/${driverId}/register`, formData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async rejectPendingDriver(driverId, reason = '') {
    try {
      const response = await apiClient.delete(`/driver/pending-registrations/${driverId}`, {
        data: { reason },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // Command Center's "Request Hospital" requests still awaiting review.
  async getPendingHospitalRequests() {
    try {
      const response = await apiClient.get('/hospital/registration-requests');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async registerHospitalFromRequest(requestId, formData) {
    try {
      const response = await apiClient.post(`/hospital/registration-requests/${requestId}/register`, formData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async uploadDriverDocument(driverId, documentData) {
    try {
      const response = await apiClient.post(`/driver/${driverId}/documents`, documentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async verifyDriverDocument(documentId, verificationData) {
    try {
      const response = await apiClient.put(`/driver/documents/${documentId}/verify`, verificationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async updateDriverStatus(driverId, statusData) {
    try {
      const response = await apiClient.put(`/driver/${driverId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ============================================================================
  // HOSPITAL REGISTRATION METHODS
  // ============================================================================
  async registerHospital(hospitalData) {
    try {
      const response = await apiClient.post('/hospital/register', hospitalData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async deleteHospital(hospitalId) {
    try {
      const response = await apiClient.delete(`/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // ============================================================================
  // GENERIC HTTP METHODS
  // ============================================================================
  async get(url, params = {}) {
    try {
      const response = await apiClient.get(url, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async post(url, data) {
    try {
      const response = await apiClient.post(url, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async put(url, data) {
    try {
      const response = await apiClient.put(url, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async patch(url, data) {
    try {
      const response = await apiClient.patch(url, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async delete(url) {
    try {
      const response = await apiClient.delete(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ============================================================================
  // DRIVER APP METHODS
  // ============================================================================

  // Profile Management
  async getDriverAppProfile() {
    try {
      const response = await apiClient.get('/driver-app/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateDriverAppProfile(profileData) {
    try {
      const response = await apiClient.put('/driver-app/profile', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Trip Management
  async getDriverTrips(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/trips', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getTripDetails(tripId) {
    try {
      const response = await apiClient.get(`/driver-app/trips/${tripId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateTripStatus(tripId, statusData) {
    try {
      const response = await apiClient.put(`/driver-app/trips/${tripId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Driver Response Management
  async respondToBookingRequest(bookingId, responseData) {
    try {
      const response = await apiClient.post(`/driver-app/booking-requests/${bookingId}/respond`, responseData);
      return response.data;
    } catch (error) {
      // Enhanced error handling for driver responses
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to respond to this booking.');
      } else if (error.response?.status === 404) {
        throw new Error('Booking request not found or already processed.');
      } else if (error.response?.status === 409) {
        throw new Error('Booking request has already been responded to.');
      } else if (error.response?.status === 408) {
        throw new Error('Request timeout. Please try again.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Failed to respond to booking request. Please try again.');
      }
    }
  },

  async getPendingRequests() {
    try {
      const response = await apiClient.get('/driver-app/pending-requests');
      return response.data;
    } catch (error) {
      // Enhanced error handling for pending requests
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Driver role required.');
      } else if (error.response?.status === 408) {
        throw new Error('Request timeout. Please check your connection.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Failed to fetch pending requests. Please try again.');
      }
    }
  },

  async getDriverResponseHistory(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/response-history', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getBookingRequestDetails(bookingId) {
    try {
      const response = await apiClient.get(`/driver-app/booking-requests/${bookingId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Location Tracking
  async updateDriverLocation(locationData) {
    try {
      const response = await apiClient.post('/driver-app/location', locationData);
      return response.data;
    } catch (error) {
      // Enhanced error handling for location updates
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Driver role required.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid location data provided.');
      } else if (error.response?.status === 408) {
        throw new Error('Request timeout. Please check your connection.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Failed to update location. Please try again.');
      }
    }
  },

  async getDriverLocationHistory(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/location/history', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Earnings Dashboard
  async getDriverEarnings(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/earnings', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getEarningsSummary(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/earnings/summary', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Payment Handling
  async generatePaymentQR(bookingId) {
    try {
      const response = await apiClient.post(`/driver-app/payments/${bookingId}/qr`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async confirmPayment(bookingId, paymentData) {
    try {
      const response = await apiClient.post(`/driver-app/payments/${bookingId}/confirm`, paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Notifications
  async getDriverNotifications(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/notifications', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateNotificationPreferences(preferences) {
    try {
      const response = await apiClient.put('/driver-app/notifications/preferences', preferences);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async markNotificationAsRead(notificationId) {
    try {
      const response = await apiClient.put(`/driver-app/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Performance & Incentives
  async getDriverPerformance(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/performance', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getDriverIncentives(filters = {}) {
    try {
      const response = await apiClient.get('/driver-app/incentives', { params: filters });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Safety Toolkit
  async sendSOSAlert(alertData) {
    try {
      const response = await apiClient.post('/driver-app/safety/sos', alertData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async shareLocation(shareData) {
    try {
      const response = await apiClient.post('/driver-app/safety/share-location', shareData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getEmergencyContacts() {
    try {
      const response = await apiClient.get('/driver-app/safety/emergency-contacts');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Settings & Preferences
  async getDriverSettings() {
    try {
      const response = await apiClient.get('/driver-app/settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateDriverSettings(settings) {
    try {
      const response = await apiClient.put('/driver-app/settings', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async changeDriverPassword(passwordData) {
    try {
      const response = await apiClient.put('/driver-app/password', passwordData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ============================================================================
  // PAYMENT METHODS
  // ============================================================================
  async initiatePayment(bookingId, fare) {
    try {
      const endpoint = `/v1/hospital/bookings/${bookingId}/initiate-payment`;
      console.log('💳 [apiService] ===== PAYMENT INITIATION START =====');
      console.log('💳 [apiService] Booking ID:', bookingId);
      console.log('💳 [apiService] Fare:', fare);
      console.log('💳 [apiService] Endpoint:', endpoint);

      const storage = getLocalStorage();
      const token = storage ? storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN) : null;
      console.log('💳 [apiService] JWT Token Key:', API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      console.log('💳 [apiService] JWT Token Present:', !!token);
      if (token) {
        console.log('💳 [apiService] Token (first 50 chars):', token.substring(0, 50));
        console.log('💳 [apiService] Token Length:', token.length);
        console.log('💳 [apiService] Token Format Valid:', token.startsWith('ey') ? '✅ Looks like JWT' : '❌ Not JWT format');
      }

      if (!token) {
        console.error('❌ [apiService] NO JWT TOKEN FOUND!');
        return {
          success: false,
          message: 'No authentication token found. Please login again.',
          status: 401,
          data: null
        };
      }

      // Generate idempotency key in format: YYYYMMDD-MM-DD (date-based format)
      // This ensures uniqueness per day while being readable
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      // Format: YYYYMMDD-MM-DD (e.g., "20250121-01-21")
      const idempotencyKey = `${year}${month}${day}-${month}-${day}`;

      // Prepare request body with fare (not amount)
      const requestBody = {
        idempotencyKey,
        fare: fare.toString()
      };

      console.log('💳 [apiService] Request body:', requestBody);
      console.log('💳 [apiService] Making v1Api.post request...');
      const response = await v1Api.post(endpoint, requestBody);
      console.log('💳 [apiService] ✅ Payment initiation SUCCESS');
      console.log('💳 [apiService] Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [apiService] Payment initiation error:', error);
      console.error('❌ [apiService] Error status:', error.response?.status);
      console.error('❌ [apiService] Error data:', error.response?.data);
      console.error('❌ [apiService] Error message:', error.message);

      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || error.response.statusText || error.message,
          status: error.response.status,
          data: null
        };
      }

      return {
        success: false,
        message: error.message || 'Payment initiation failed',
        status: 0,
        data: null
      };
    }
  },

  async verifyPayment(paymentData) {
    try {
      const endpoint = '/v1/hospital/payments/verify';
      console.log('💳 [apiService] Verifying payment');
      console.log('💳 [apiService] Endpoint:', endpoint);
      console.log('💳 [apiService] Payment data:', paymentData);

      const storage = getLocalStorage();
      const token = storage ? storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN) : null;
      console.log('💳 [apiService] JWT Token present:', !!token, 'Length:', token?.length || 0);

      if (!token) {
        console.error('❌ [apiService] NO JWT TOKEN FOUND!');
        return {
          success: false,
          message: 'No authentication token found. Please login again.',
          status: 401,
          data: null
        };
      }

      const response = await v1Api.post(endpoint, paymentData);
      console.log('💳 [apiService] Payment verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [apiService] Payment verification error:', error);
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || error.message,
          status: error.response.status,
          data: null
        };
      }
      throw error;
    }
  },

  async getBookingDetails(bookingId) {
    try {
      const response = await apiClient.get(`/bookings/details/${bookingId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ============================================================================
  // GENERIC HTTP METHODS (for admin operations and other generic endpoints)
  // ============================================================================

  /**
   * Generic GET request
   * @param {string} endpoint - The API endpoint (e.g., '/admin/partnership-requests')
   * @param {object} config - Optional axios config (params, headers, etc.)
   * @returns {Promise} Response data with success flag
   */
  async get(endpoint, config = {}) {
    try {
      const response = await apiClient.get(endpoint, config);
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error(`GET ${endpoint} error:`, error);
      console.error(`GET ${endpoint} error details:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Request failed',
        status: error.response?.status || 500,
        data: null,
        error: error.response?.data // Include full error data for debugging
      };
    }
  },

  /**
   * Generic POST request
   * @param {string} endpoint - The API endpoint
   * @param {object} data - Request body data
   * @param {object} config - Optional axios config
   * @returns {Promise} Response data with success flag
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await apiClient.post(endpoint, data, config);
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error(`POST ${endpoint} error:`, error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Request failed',
        status: error.response?.status || 500,
        data: null
      };
    }
  },

  /**
   * Generic PUT request
   * @param {string} endpoint - The API endpoint
   * @param {object} data - Request body data
   * @param {object} config - Optional axios config
   * @returns {Promise} Response data with success flag
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await apiClient.put(endpoint, data, config);
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error(`PUT ${endpoint} error:`, error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Request failed',
        status: error.response?.status || 500,
        data: null
      };
    }
  },

  /**
   * Generic DELETE request
   * @param {string} endpoint - The API endpoint
   * @param {object} config - Optional axios config
   * @returns {Promise} Response data with success flag
   */
  async delete(endpoint, config = {}) {
    try {
      const response = await apiClient.delete(endpoint, config);
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error(`DELETE ${endpoint} error:`, error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Request failed',
        status: error.response?.status || 500,
        data: null
      };
    }
  }
};
// Export both named and default exports
export default apiService;
